/** Matches calendar dates like `2026-07-21` (no time). */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a date/datetime for display.
 * Date-only strings are treated as local calendar days (not UTC midnight),
 * avoiding off-by-one in US timezones from `new Date('YYYY-MM-DD')`.
 */
export function parseDisplayDate(value: string): Date {
  if (DATE_ONLY_RE.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y!, (m ?? 1) - 1, d ?? 1, 12, 0, 0, 0);
  }
  return new Date(value);
}

export function formatCurrency(value: string | number | null | undefined): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    Number(value ?? 0),
  );
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parseDisplayDate(value));
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parseDisplayDate(value));
}

export function formatRelativeTime(value: string | null | undefined): string {
  if (!value) return '';
  const diffMs = Date.now() - parseDisplayDate(value).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(value);
}

export function prettyLabel(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  return value.replace(/_/g, ' ');
}
